/**
 * features/finance/financeWrite.ts
 * ------------------------------------------------------------------
 * Centralised finance writes — mirrors features/hr/hrWrite.ts so the
 * conventions are identical: timestamps always stamped, the wrapped
 * (undefined-stripping) setDoc/addDoc/updateDoc from @/lib/firebase,
 * and audit-coupled patterns in one place.
 *
 * Demo build: direct client writes (allowed by the finance rules
 * block). Production scoping moves the sensitive writes (billing run,
 * payment capture, void) behind a Cloud Function — exactly as the HR
 * module is structured.
 * ------------------------------------------------------------------ */
import { collection, doc, deleteDoc } from 'firebase/firestore';
import { db, setDoc, addDoc, updateDoc } from '@/lib/firebase';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';

const now = () => new Date().toISOString();

export function newId(collectionName: string): string {
  return doc(collection(db, collectionName)).id;
}

export async function financeSet<T extends Record<string, unknown>>(
  collectionName: string, id: string, data: T,
): Promise<void> {
  await setDoc(doc(db, collectionName, id), {
    ...data,
    createdAt: (data as { createdAt?: string }).createdAt ?? now(),
    updatedAt: now(),
  });
}

export async function financeAdd<T extends Record<string, unknown>>(
  collectionName: string, data: T,
): Promise<string> {
  const ref = await addDoc(collection(db, collectionName), { ...data, createdAt: now(), updatedAt: now() });
  return ref.id;
}

export async function financeUpdate<T extends Record<string, unknown>>(
  collectionName: string, id: string, patch: T,
): Promise<void> {
  await updateDoc(doc(db, collectionName, id), { ...patch, updatedAt: now() });
}

export async function financeDelete(collectionName: string, id: string): Promise<void> {
  await deleteDoc(doc(db, collectionName, id));
}

/* ---- Per-collection convenience wrappers ------------------------- */
export const finance = {
  feeItems: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.feeItems, id, data),
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.feeItems, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.feeItems, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.feeItems, id),
  },
  staffChildren: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.staffChildren, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.staffChildren, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.staffChildren, id),
  },
  prepayFamilies: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.prepayFamilies, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.prepayFamilies, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.prepayFamilies, id),
  },
  bursaryTypes: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.bursaryTypes, id, data),
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.bursaryTypes, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.bursaryTypes, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.bursaryTypes, id),
  },
  bursaryAwards: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.bursaryAwards, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.bursaryAwards, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.bursaryAwards, id),
  },
  feeStructures: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.feeStructures, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.feeStructures, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.feeStructures, id),
  },
  studentFeeOptions: {
    set: (studentId: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.studentFeeOptions, studentId, data),
    update: (studentId: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.studentFeeOptions, studentId, patch),
  },
  invoices: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.invoices, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.invoices, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.invoices, id),
  },
  payments: {
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.payments, data),
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.payments, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.payments, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.payments, id),
  },
  unidentifiedPayments: {
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.unidentifiedPayments, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.unidentifiedPayments, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.unidentifiedPayments, id),
  },
  discountRules: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.discountRules, id, data),
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.discountRules, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.discountRules, id, patch),
    delete: (id: string) => financeDelete(FINANCE_COLLECTIONS.discountRules, id),
  },
  settings: {
    set: (data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.settings, 'school', data),
    update: (patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.settings, 'school', patch),
  },
  auditLog: {
    add: (data: Record<string, unknown>) => financeAdd(FINANCE_COLLECTIONS.auditLog, data),
  },
  reconciliations: {
    set: (id: string, data: Record<string, unknown>) => financeSet(FINANCE_COLLECTIONS.reconciliations, id, data),
    update: (id: string, patch: Record<string, unknown>) => financeUpdate(FINANCE_COLLECTIONS.reconciliations, id, patch),
  },
  academicHolds: {
    set: (familyId: string, data: Record<string, unknown>) =>
      financeSet(FINANCE_COLLECTIONS.academicHolds, familyId, data),
    delete: (familyId: string) => financeDelete(FINANCE_COLLECTIONS.academicHolds, familyId),
  },
};
